import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

export interface NavItem {
  to: string;
  label: string;
  icon: string;
}

/**
 * Sidebar shell — same skeleton as servicedesk-portal's Shell.tsx, plus a
 * persistent district-scope strip above every page's content. Unlike
 * ServiceDesk (whose COUNTER_OFFICER/OFFICE_MANAGER roles are NOT scoped to
 * a specific office — see its Console.tsx office picker), M41 hard-enforces
 * per-district staff membership server-side
 * (service/permissions.ts#requireDistrictStaff): a Beau Vallon DA_OFFICER
 * gets a genuine 403 on every mutating (and several read) endpoint for any
 * other district. So this portal deliberately has NO cross-district office
 * picker — the signed-in persona's own `district` is shown as a fixed,
 * non-interactive fact, not a dropdown that would mostly error out.
 */
export function Shell({ title, items }: { title: string; items: NavItem[] }) {
  const { persona, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen">
      <aside className="fixed inset-y-0 left-0 flex w-60 flex-col bg-ink-950">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-600 text-sm font-black text-white">SC</div>
          <div>
            <div className="text-sm font-bold leading-tight text-white">One Gov Seychelles</div>
            <div className="text-[11px] text-slate-400">{title}</div>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 px-3 py-2">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to.split('/').length <= 2}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
                  isActive ? 'bg-brand-600 text-white' : 'text-slate-300 hover:bg-ink-800 hover:text-white'
                }`
              }
            >
              <span className="w-5 text-center">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-ink-800 px-5 py-4">
          <div className="text-sm font-semibold text-white">{persona?.name}</div>
          <div className="text-[11px] leading-snug text-slate-400">{persona?.title}</div>
          <div className="text-[11px] leading-snug text-slate-500">{persona?.org}</div>
          {persona?.district ? (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-brand-600/20 px-2 py-1 text-[11px] font-semibold text-brand-100 ring-1 ring-brand-600/40">
              <span aria-hidden>⌂</span> {persona.district} District
            </div>
          ) : null}
          <button
            className="mt-3 w-full rounded-lg border border-ink-700 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-ink-800"
            onClick={() => {
              logout();
              navigate('/');
            }}
          >
            Switch user
          </button>
        </div>
      </aside>
      <main className="ml-60 flex-1 px-8 py-7">
        {persona?.district ? (
          <div className="mb-5 flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3.5 py-2 text-xs font-semibold text-brand-700">
            <span aria-hidden>⌂</span>
            Scoped to {persona.district} District Administration Office — the API enforces this by NIN-matched active
            staff membership; you cannot view or act on another district's records.
          </div>
        ) : (
          <div className="mb-5 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2 text-xs font-semibold text-amber-700">
            <span aria-hidden>⚠</span>
            This account has no district binding on file (see server/staff-access.ts) — every district-scoped call
            below will be rejected with 403 until a StaffGrant sets one.
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
